import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import hosys from "@/api/api";
import { autoMatch } from "./fuzzyService";
import { invoiceLog } from "./invoiceLogger";
import {
  CheckCircle2, AlertTriangle, X, Link2, ChevronDown,
  ChevronUp, Package, Search
} from "lucide-react";

// Confidence bar
function ConfBar({ value }) {
  const color = value >= 70 ? "bg-emerald-500" : value >= 40 ? "bg-amber-400" : "bg-red-400";
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${value}%` }} />
      </div>
      <span className="text-xs font-bold text-gray-600 w-8 text-right">{value}%</span>
    </div>
  );
}

// Alternative suggestions dropdown
function AlternativesPicker({ alternatives, ingredients, onSelect }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const filtered = search
    ? ingredients.filter(i => i.name.toLowerCase().includes(search.toLowerCase())).slice(0, 8)
    : null;

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 text-xs text-[#7B2D3F] font-semibold hover:underline"
      >
        <Search className="w-3 h-3" /> Cercar ingredient {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>
      {open && (
        <div className="absolute top-6 left-0 z-30 w-72 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden">
          <div className="p-2 border-b">
            <input
              autoFocus
              type="text"
              placeholder="Cercar ingredient..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full text-xs border border-gray-200 rounded-lg px-2 py-1.5 outline-none focus:border-[#7B2D3F]"
            />
          </div>
          <div className="max-h-48 overflow-y-auto">
            {/* Suggestions */}
            {!search && alternatives.length > 0 && (
              <>
                <p className="px-3 py-1.5 text-[10px] font-bold text-gray-400 uppercase bg-gray-50">Suggeriments</p>
                {alternatives.map((a, i) => (
                  <button
                    key={`alert-${i}`}
                    onClick={() => { onSelect(a.ingredient); setOpen(false); }}
                    className="w-full flex items-center justify-between px-3 py-2 hover:bg-gray-50 text-left"
                  >
                    <span className="text-xs font-medium text-gray-800">{a.ingredient.name}</span>
                    <Badge className={`border-0 text-[10px] ${a.confidence >= 40 ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-600"}`}>
                      {a.confidence}%
                    </Badge>
                  </button>
                ))}
                <div className="border-t border-gray-100" />
              </>
            )}
            {/* Search results */}
            {filtered && filtered.map(ing => (
              <button
                key={ing.id}
                onClick={() => { onSelect(ing); setOpen(false); setSearch(""); }}
                className="w-full px-3 py-2 hover:bg-gray-50 text-left text-xs font-medium text-gray-800"
              >
                {ing.name}
              </button>
            ))}
            {filtered && filtered.length === 0 && (
              <p className="px-3 py-3 text-xs text-gray-400 text-center">Cap resultat</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function StockMatcher({ items, invoiceData, venueId, currentUser, onConfirm, onSkip }) {
  const [ingredients, setIngredients] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(true);

  useEffect(() => {
    if (!venueId) return;
    const stop = invoiceLog.startTimer("stockMatch");
    hosys.entities.Ingredient.filter({ venue_id: venueId, active: true })
      .then(list => {
        setIngredients(list);
        const ms = autoMatch(items || [], list);
        setMappings(ms);
        const matched = ms.filter(m => m.matched).length;
        invoiceLog.info("stockMatch", `Auto-matched ${matched}/${ms.length} items`, ms.map(m => ({
          invoice: m.item.description, matched: m.matched?.name, conf: m.confidence
        })));
        stop();
      })
      .catch(e => { invoiceLog.error("stockMatch", "Failed to load ingredients", { error: e.message }); })
      .finally(() => setLoading(false));
  }, [venueId, items]);

  const setMatch = (i, ing) => {
    const newM = [...mappings];
    newM[i] = { ...newM[i], matched: ing, ingredientId: ing?.id || null };
    setMappings(newM);
  };

  const toggleSkip = (i) => {
    const newM = [...mappings];
    newM[i] = { ...newM[i], skip: !newM[i].skip };
    setMappings(newM);
  };

  const handleConfirm = () => {
    const movements = mappings
      .filter(m => !m.skip && m.item)
      .map(m => ({
        venue_id: venueId,
        invoice_product_name: m.item.description || "—",
        matched_product_name: m.matched?.name || null,
        ingredient_id: m.ingredientId || null,
        match_confidence: m.confidence || 0,
        quantity: m.item.quantity || 0,
        unit: m.item.unit || "",
        unit_price: m.item.unit_price || 0,
        line_total: m.item.line_total || 0,
        supplier_name: invoiceData.supplier_name || "",
        movement_date: invoiceData.invoice_date || new Date().toISOString().split("T")[0],
        movement_type: "purchase",
        confirmed_by: currentUser?.email || "",
      }));
    invoiceLog.info("stockMatch", `Confirming ${movements.length} stock movements`);
    onConfirm(movements);
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-48 gap-3">
        <div className="w-8 h-8 border-2 border-[#7B2D3F] border-t-transparent rounded-full animate-spin" />
        <p className="text-sm text-gray-500">Cercant coincidències al catàleg...</p>
      </div>
    );
  }

  if (!items || items.length === 0) {
    return (
      <div className="text-center py-12 space-y-3">
        <Package className="w-10 h-10 text-gray-300 mx-auto" />
        <p className="text-gray-500 font-medium">No s'han detectat línies de producte</p>
        <Button variant="outline" onClick={onSkip}>Continuar sense actualitzar estoc</Button>
      </div>
    );
  }

  const activeCount = mappings.filter(m => !m.skip).length;
  const matchedCount = mappings.filter(m => !m.skip && m.matched).length;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-bold text-gray-900 text-lg">Mapeig de productes a estoc</h3>
          <p className="text-sm text-gray-500">
            {matchedCount} de {mappings.length} productes han coincidit automàticament
          </p>
        </div>
        <div className="flex gap-2">
          <Badge className={`border-0 text-xs ${matchedCount === mappings.length ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
            {matchedCount}/{mappings.length} coincidències
          </Badge>
        </div>
      </div>

      {/* Summary bar */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: "Alt confiança", value: mappings.filter(m => m.confidence >= 70).length, color: "text-emerald-700 bg-emerald-50" },
          { label: "Confiança mitja", value: mappings.filter(m => m.confidence >= 40 && m.confidence < 70).length, color: "text-amber-700 bg-amber-50" },
          { label: "Sense coincidència", value: mappings.filter(m => m.confidence < 40).length, color: "text-gray-600 bg-gray-50" },
        ].map(s => (
          <div key={s.label} className={`rounded-xl p-3 border border-gray-100 text-center ${s.color}`}>
            <p className="text-lg font-bold">{s.value}</p>
            <p className="text-[10px] font-semibold uppercase tracking-wide opacity-70">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Mappings */}
      <div className="space-y-2.5">
        {mappings.map((m, i) => (
          <div
            key={`member-${i}`}
            className={`border rounded-xl p-4 transition-all ${
              m.skip ? "opacity-40 bg-gray-50 border-gray-200" :
              m.matched && m.confidence >= 70 ? "border-emerald-200 bg-emerald-50/30" :
              m.matched ? "border-amber-200 bg-amber-50/20" :
              "border-gray-200 bg-white"
            }`}
          >
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 text-sm">{m.item.description || "—"}</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {m.item.quantity} {m.item.unit} · {m.item.unit_price?.toFixed(2)}€/u · Total: <span className="font-semibold text-gray-700">{m.item.line_total?.toFixed(2)}€</span>
                  {m.item.vat_pct ? ` · IVA ${m.item.vat_pct}%` : ""}
                </p>
              </div>
              <button
                onClick={() => toggleSkip(i)}
                className={`flex-shrink-0 p-1.5 rounded-lg transition-colors ${m.skip ? "bg-gray-200 text-gray-500" : "text-gray-400 hover:bg-gray-100"}`}
                title={m.skip ? "Activar" : "Ignorar"}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>

            {!m.skip && (
              <div className="border-t border-gray-100 pt-2.5 mt-2">
                <div className="flex items-center gap-2 mb-1.5">
                  <Link2 className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                  {m.matched ? (
                    <div className="flex-1 flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-semibold text-gray-800">{m.matched.name}</span>
                      <button onClick={() => setMatch(i, null)} className="text-[10px] text-red-500 hover:text-red-700 underline">
                        Desvincula
                      </button>
                    </div>
                  ) : (
                    <span className="text-xs text-gray-400 italic">Sense coincidència — selecciona manualment</span>
                  )}
                </div>
                <ConfBar value={m.confidence} />
                <div className="mt-2">
                  <AlternativesPicker
                    alternatives={m.alternatives || []}
                    ingredients={ingredients}
                    onSelect={(ing) => setMatch(i, ing)}
                  />
                </div>
              </div>
            )}
            {m.skip && <p className="text-xs text-gray-400 italic mt-1">Ignorat — no actualitzarà estoc</p>}
          </div>
        ))}
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-2 border-t border-gray-100">
        <Button variant="outline" onClick={onSkip} className="text-gray-600">
          Saltar
        </Button>
        <Button
          className="bg-[#7B2D3F] hover:bg-[#6a2636] text-white flex-1"
          onClick={handleConfirm}
        >
          Confirmar i actualitzar estoc ({activeCount} línies)
        </Button>
      </div>
    </div>
  );
}